/**
 * Google Picker SDK + Google Identity Services loader.
 *
 * Lazily injects the two Google script tags on first call and resolves
 * when both are ready. Subsequent calls return the same promise, so the
 * SDKs are loaded at most once per page lifetime.
 *
 * CSP NOTE: The host app's Content-Security-Policy must allow:
 *   script-src  https://apis.google.com https://accounts.google.com;
 *   frame-src   https://docs.google.com https://accounts.google.com;
 * This is an ops/deploy-config change — do NOT modify CSP from this file.
 */

// The google-picker.d.ts ambient declaration is picked up automatically by TypeScript.
// No explicit import needed.

let loadPromise: Promise<void> | null = null;

/**
 * Injects a script tag and resolves when it loads.
 */
function injectScript(src: string, id: string): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    if (document.getElementById(id)) {
      // Already injected (e.g. fast-refresh in dev)
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.id = id;
    script.src = src;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () =>
      reject(new Error(`Failed to load Google SDK: ${src}. Check CSP headers.`));
    document.head.appendChild(script);
  });
}

/**
 * Loads the Google API (gapi) script and initialises the picker client.
 */
function loadGapi(): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    injectScript('https://apis.google.com/js/api.js', 'gapi-script')
      .then(() => {
        if (!window.gapi) {
          reject(new Error('gapi not available after script load'));
          return;
        }
        window.gapi.load('picker', {
          callback: resolve,
          onerror: reject,
        });
      })
      .catch(reject);
  });
}

/**
 * Loads the Google Identity Services (GIS) client script.
 * The GIS library attaches itself to `window.google.accounts` when loaded.
 */
function loadGis(): Promise<void> {
  return injectScript(
    'https://accounts.google.com/gsi/client',
    'gsi-client-script',
  );
}

/**
 * Lazy-loads both Google Picker SDK and GIS client.
 * Safe to call multiple times — only ever creates one load sequence.
 *
 * @throws Error if either script fails to load (likely a CSP violation).
 */
export function loadPickerSdk(): Promise<void> {
  if (loadPromise) {
    return loadPromise;
  }

  loadPromise = Promise.all([loadGapi(), loadGis()]).then(() => {
    // Verify the Picker API surface is available
    if (!window.gapi?.picker || !window.google?.accounts?.oauth2) {
      loadPromise = null; // Allow retry
      throw new Error('Google Picker or GIS APIs are not available after load.');
    }
  });

  return loadPromise;
}
